/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { URLProvidingEffectClass } from "types/AudioEffect";
import { Asset, AssetId, AssetSet } from "types/Asset";
import { PlaylistClip } from "types/Clip";
import gatewayFetch from "utilities/gatewayFetch";
import { AssetUrlType, IAssetUrlCache } from "types/AssetUrlCache";

export class AssetUrlCache implements IAssetUrlCache {
  private assets: AssetSet = {};
  private assetUrlCache: Partial<Record<AssetId, Promise<string>>> = {};

  private readonly localAudioAssetBlobUrls: Record<AssetId, string> = {};

  setAssets(assets: AssetSet): void {
    // TODO: if the assets change enough we might need to evict things from the cache
    this.assets = assets;
  }

  getAsset(assetId: AssetId): Asset {
    return this.assets[assetId];
  }

  async getClipUrl(
    clip: PlaylistClip,
    urlProvider: URLProvidingEffectClass,
    type: AssetUrlType = AssetUrlType.AUDIO
  ): Promise<string> {
    const asset = this.assets[clip.assetId];
    if (!asset) {
      throw new Error(`Asset not found: ${clip.assetId}`);
    }

    if (
      urlProvider.canUseLocalAsset() &&
      this.localAudioAssetBlobUrls[asset.id] !== undefined
    ) {
      const localUrl = this.localAudioAssetBlobUrls[asset.id];
      // _findOrInitializeElementForClip uses some self-referential promises as it sets the clipAudio
      // this promise here lets those settle, otherwise some of the self references would be undefined
      await new Promise((resolve) => setTimeout(resolve, 0));
      return localUrl;
    } else {
      const expiringUrl = await this.getExpiringUrl(
        clip,
        asset,
        urlProvider,
        type
      );
      return expiringUrl;
    }
  }

  invalidateClipUrl(
    clip: PlaylistClip,
    urlProvider: URLProvidingEffectClass,
    type: AssetUrlType
  ): void {
    const asset = this.assets[clip.assetId];
    delete this.assetUrlCache[this.getCacheKey(asset, urlProvider, type)];
  }

  addLocalAsset(assetId: AssetId, blob: Blob): void {
    this.localAudioAssetBlobUrls[assetId] = URL.createObjectURL(blob);
  }

  hasLocalAsset(clip: PlaylistClip): boolean {
    return this.localAudioAssetBlobUrls[clip.assetId] !== undefined;
  }

  canLoadClip(clip: PlaylistClip): boolean {
    const asset = this.assets[clip.assetId];
    if (!asset) {
      throw new Error(`Asset not found: ${clip.assetId}`);
    }

    return this.hasLocalAsset(clip) || asset.url !== undefined;
  }

  reset(): void {
    Object.values(this.localAudioAssetBlobUrls).forEach((blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
  }

  private getCacheKey(
    asset: Asset,
    urlProvider: URLProvidingEffectClass,
    type: AssetUrlType
  ): string {
    return `${asset.id}-${urlProvider.name}-${type}`;
  }

  private async getExpiringUrl(
    clip: PlaylistClip,
    asset: Asset,
    urlProvider: URLProvidingEffectClass,
    type: AssetUrlType
  ): Promise<string> {
    const cacheKey = this.getCacheKey(asset, urlProvider, type);
    let existingUrlPromise = this.assetUrlCache[cacheKey];
    if (existingUrlPromise !== undefined) {
      return existingUrlPromise;
    }

    const nonExpiringUrl = urlProvider.providePermanentUrl(clip, asset, type);
    if (nonExpiringUrl) {
      this.assetUrlCache[cacheKey] = (async (): Promise<string> => {
        // Video files might not have a non-expiring URL yet if only just uploaded
        const expiringUrlResponse = await gatewayFetch(nonExpiringUrl!, {});
        const { url: expiringUrl } = await expiringUrlResponse.json();
        return expiringUrl;
      })();
    }

    return this.assetUrlCache[cacheKey] || "";
  }
}
